import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Calendar, ArrowRight, Loader2 } from "lucide-react";
import { format, parseISO } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Blog | Move On Packers & Movers";
    axios.get(`${API}/blog`).then(({ data }) => setPosts(data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <section className="py-16 lg:py-24 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-semibold tracking-[0.18em] uppercase text-[#081537] mb-3">Blog</div>
          <h1 className="font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#081537] text-balance max-w-3xl">
            Moving smarter, one guide at a time.
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Practical guides, pricing insights and tips from 12+ years of relocating homes and offices across Kerala.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="py-20 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading posts...</div>
          ) : posts.length === 0 ? (
            <div className="py-20 text-center text-slate-500">No posts yet. Check back soon!</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {posts.map((p) => (
                <BlogCard key={p.slug} post={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function BlogCard({ post }) {
  const dt = post.published_at ? parseISO(typeof post.published_at === "string" ? post.published_at : post.published_at) : null;
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-[#081537]/30 hover:shadow-xl hover:shadow-blue-900/5 transition-all"
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
          {dt && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {format(dt, "MMM d, yyyy")}</span>}
          {post.tags?.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-slate-300" />
              {post.tags[0]}
            </span>
          )}
        </div>
        <h3 className="font-[Outfit] text-xl font-semibold text-[#081537] leading-snug group-hover:text-[#0F2152]">
          {post.title}
        </h3>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
          {post.excerpt}
        </p>
        <div className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#081537] group-hover:gap-2 transition-all">
          Read more <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}
